"use client";
// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.

// Wingo UI Pro · https://wingo-ui.com/legal/license

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ComponentProps, type MutableRefObject, type ReactNode, type Ref, type RefObject } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ChevronDown, ChevronLeft, Ellipsis } from "lucide-react";
import { Avatar, type AvatarRadius } from "@/components/wingo/ui/avatar";
import { Breadcrumb, type BreadcrumbEntry } from "@/components/wingo/ui/breadcrumb";
import { Button, type ButtonVariant } from "@/components/wingo/ui/button";
import { DropdownMenu, type MenuEntry } from "@/components/wingo/ui/dropdown-menu";
import { Skeleton } from "@/components/wingo/ui/skeleton";
import { useIsMobile } from "@/components/wingo/hooks/use-media-query";
import { useOverflowItems } from "@/components/wingo/hooks/use-overflow-items";
import { findScrollParent, useScrollContainer, type ScrollTarget } from "@/components/wingo/hooks/use-scroll-state";
import { durations, easings, springs, usePrefersReducedMotion } from "@/components/wingo/lib/motion";
import { NavLink, useNavigate, type LinkComponent } from "@/components/wingo/lib/navigation";
import { colorVars, toneClasses, type Tone } from "@/components/wingo/lib/tones";
import { cn } from "@/components/wingo/lib/utils";
import { withRef } from "@/components/wingo/lib/with-ref";
import { useDefaults } from "@/components/wingo/lib/ui-config";

export type PageHeaderSize = "sm" | "md" | "lg";
export type PageHeaderSticky = "none" | "condensed";
export type PageHeaderMobileActions = "menu" | "stack";
export type PageHeaderSlot =
  | "breadcrumb"
  | "row"
  | "back"
  | "media"
  | "heading"
  | "title"
  | "badges"
  | "description"
  | "meta"
  | "metaItem"
  | "actions"
  | "more"
  | "tabs"
  | "condensed"
  | "condensedTitle"
  | "condensedAction";
export type { Tone as PageHeaderTone };

export type PageHeaderAction = {
  // stable key; default the label
  id?: string;
  label: string;
  icon?: ReactNode;
  // a returned promise shows the button loading until it settles
  onSelect?: () => void | Promise<unknown>;
  // renders a link (through NavLink and the router's link component)
  href?: string;
  // default outline, solid for the primary one
  variant?: ButtonVariant;
  tone?: Tone;
  // always visible, last in the row; without one, the last solid action is the primary
  primary?: boolean;
  disabled?: boolean;
  // Extensie locală TLS (nu există în wingo): acțiunea deschide un meniu cu aceste intrări — un buton cu chevron în
  // rând, un submeniu când e pliată în „⋯".
  items?: MenuEntry[];
};

export type PageHeaderMeta = { id?: string; icon?: ReactNode; label: ReactNode };

export type PageHeaderAvatar = {
  src?: string;
  name: string;
  // md for companies (a rounded square), full for people
  radius?: AvatarRadius;
};

// ---------------------------------------------------------------- classes

// the title, 18 / 24 / 32px on desktop and 18 / 22 / 26px on phones, with explicit line heights so the back button
// can center on the first line
const TITLE_SIZES: Record<PageHeaderSize, string> = {
  sm: "text-lg/6",
  md: "text-[22px]/7 md:text-2xl/8",
  lg: "text-[26px]/8 md:text-[32px]/10",
};

// the height of the title's first line, for things that sit next to it
const LINE_HEIGHTS: Record<PageHeaderSize, string> = {
  sm: "h-6",
  md: "h-7 md:h-8",
  lg: "h-8 md:h-10",
};

const TILE_SIZES: Record<PageHeaderSize, string> = {
  sm: "size-9 rounded-[12px] [&_svg:not([class*='size-'])]:size-[18px]",
  md: "size-10 rounded-2xl [&_svg:not([class*='size-'])]:size-5",
  lg: "size-12 rounded-2xl [&_svg:not([class*='size-'])]:size-6",
};

const TILE_HEIGHTS: Record<PageHeaderSize, string> = { sm: "h-9", md: "h-10", lg: "h-12" };

// px; the avatar is a step larger than the icon tile (a logo or a face reads better big)
const AVATAR_SIZES: Record<PageHeaderSize, number> = { sm: 36, md: 48, lg: 56 };
const AVATAR_HEIGHTS: Record<PageHeaderSize, string> = { sm: "h-9", md: "h-12", lg: "h-14" };

const TITLE_SKELETONS: Record<PageHeaderSize, string> = {
  sm: "h-5 w-40",
  md: "h-6 w-48 md:h-7 md:w-60",
  lg: "h-7 w-56 md:h-9 md:w-72",
};

// glass bars (W3 BAR_GLASS): --bar-bg is the surface the bar sits on
const BAR_GLASS =
  "bg-[color-mix(in_oklab,var(--bar-bg,rgb(var(--wui-background)))_82%,transparent)] backdrop-blur-xl backdrop-saturate-150 [@supports_not_(backdrop-filter:blur(1px))]:bg-[color:var(--bar-bg,rgb(var(--wui-background)))]";

const BACK_LINK =
  "relative -ms-1 inline-flex min-w-0 max-w-full cursor-pointer touch-manipulation select-none items-center gap-0.5 rounded-[6px] text-sm font-medium text-muted-foreground outline-hidden [-webkit-tap-highlight-color:transparent] transition-colors duration-fast ease-smooth hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline focus-visible:outline-ring pointer-coarse:after:absolute pointer-coarse:after:-inset-x-2 pointer-coarse:after:-inset-y-3";

const SIZE_KEYS: PageHeaderSize[] = ["sm", "md", "lg"];

const filled = (node: ReactNode) => node !== undefined && node !== null && node !== false && node !== "";

const actionKey = (action: PageHeaderAction, index: number) => action.id ?? `${index}-${action.label}`;

function isThenable(value: unknown): value is PromiseLike<unknown> {
  return typeof value === "object" && value !== null && typeof (value as PromiseLike<unknown>).then === "function";
}

// ---------------------------------------------------------------- PageHeader

export type PageHeaderProps = Omit<ComponentProps<"header">, "title"> & {
  // the h1
  title?: ReactNode;
  // one or two lines under the title
  description?: ReactNode;
  // entries render a Breadcrumb (with its phone back link); a node renders as is
  breadcrumb?: BreadcrumbEntry[] | ReactNode;
  // a back button before the title; below 640px it replaces the breadcrumb
  backHref?: string;
  onBack?: () => void;
  // the back button's aria-label, and its text on phones
  backLabel?: string;
  // a tinted tile before the title
  icon?: ReactNode;
  // a photo or initials before the title (wins over icon)
  avatar?: PageHeaderAvatar;
  // after the title: status badges
  badges?: ReactNode;
  // small facts in a wrapping row under the description
  meta?: PageHeaderMeta[] | ReactNode;
  // data: the primary stays visible, the rest fold into "⋯". a node renders as is (no folding)
  actions?: PageHeaderAction[] | ReactNode;
  // NavTabs (or a Tabs list) sitting on the bottom border
  tabs?: ReactNode;
  // title 18 / 24 / 32px (phones 18 / 22 / 26px)
  size?: PageHeaderSize;
  // the media's color: the icon tile's tint, or the avatar's initials (neutral keeps the name-based color)
  tone?: Tone;
  // any css color for the icon tile or the avatar initials; replaces the tone
  color?: string;
  // the hairline under the header
  border?: boolean;
  // condensed: a glass bar with the title and the primary action pins under the top bar once the header scrolls away
  sticky?: PageHeaderSticky;
  // the compact layout (header under 512px wide): secondary actions in the "⋯" menu (a sheet on phones), or stacked full width
  mobileActions?: PageHeaderMobileActions;
  // the most buttons in the wide row, the primary included; the rest always sit in "⋯". 0 (default): as many as fit
  maxActions?: number;
  // skeletons for the title, description, meta and actions
  loading?: boolean;
  // the "⋯" button's aria-label and the action sheet's title
  moreLabel?: string;
  // the cancel row of the phone action sheet
  cancelLabel?: string;
  // what scrolls, for the condensed bar. default: the nearest ScrollContainerProvider, else the window
  scrollTarget?: ScrollTarget;
  // the router link for actions and the back link; default: the LinkProvider's
  linkComponent?: LinkComponent;
  classNames?: Partial<Record<PageHeaderSlot, string>>;
  // extra content between the title row and the tabs (filters, a progress bar)
  children?: ReactNode;
};

// outside the component: the react compiler forbids writing to a value returned from a hook
function setRef<T>(ref: Ref<T> | undefined, node: T) {
  if (typeof ref === "function") return ref(node);
  if (ref) (ref as MutableRefObject<T>).current = node;
}

function PageHeaderRender(pageHeaderProps: PageHeaderProps) {
  const {
    title = "",
    description = "",
    breadcrumb,
    backHref,
    onBack,
    backLabel = "Back",
    icon,
    avatar,
    badges,
    meta,
    actions,
    tabs,
    size = "md",
    tone = "neutral",
    color,
    border = true,
    sticky = "none",
    mobileActions = "menu",
    maxActions = 0,
    loading = false,
    moreLabel = "More actions",
    cancelLabel = "Cancel",
    scrollTarget,
    linkComponent,
    classNames,
    className,
    children,
    ref,
    ...props
  } = useDefaults("page-header", pageHeaderProps);
  const scale: PageHeaderSize = SIZE_KEYS.includes(size) ? size : "md";
  const isMobile = useIsMobile();
  const reduced = usePrefersReducedMotion();
  const navigate = useNavigate();
  const provided = useScrollContainer();

  // ---- actions: split into the primary (pinned, last) and the secondaries (folding, in order)
  const list = Array.isArray(actions) ? (actions as PageHeaderAction[]) : null;
  const customActions = !list && filled(actions as ReactNode) ? (actions as ReactNode) : null;
  let primaryIndex = list ? list.findIndex((a) => a.primary) : -1;
  if (list && primaryIndex < 0) {
    for (let i = list.length - 1; i >= 0; i--)
      if (list[i].variant === "solid") {
        primaryIndex = i;
        break;
      }
  }
  const primary = list && primaryIndex >= 0 ? list[primaryIndex] : null;
  const primaryKey = primary ? actionKey(primary, primaryIndex) : "";
  const allSecondaries = list ? list.map((action, index) => ({ action, key: actionKey(action, index) })).filter((_, i) => i !== primaryIndex) : [];
  // maxActions: the secondaries past the limit never render as buttons, they always sit in "⋯"
  const inlineLimit =
    typeof maxActions === "number" && Number.isFinite(maxActions) && maxActions >= 1
      ? Math.max(0, Math.floor(maxActions) - (primary ? 1 : 0))
      : Infinity;
  const secondaries = allSecondaries.slice(0, inlineLimit);
  const folded = allSecondaries.slice(secondaries.length);
  const count = secondaries.length + (primary ? 1 : 0);

  const { containerRef, getItemProps, getMoreProps, hiddenIndexes } = useOverflowItems({
    count,
    collapseOrder: "end",
    pinned: primary ? [secondaries.length] : undefined,
    // the wide row is display:none in the compact layout, which folds everything with css
    disabled: isMobile || loading,
  });

  // the row never gets narrower than the pinned primary plus the "⋯" trigger; the title block gives way instead
  const actionsRow = useRef<HTMLDivElement | null>(null);
  const setActionsRow = useCallback(
    (el: HTMLDivElement | null) => {
      actionsRow.current = el;
      return containerRef(el);
    },
    [containerRef],
  );
  const hasSecondaries = allSecondaries.length > 0;
  useLayoutEffect(() => {
    const row = actionsRow.current;
    if (!row || typeof ResizeObserver === "undefined") return;
    const pinned = row.querySelector<HTMLElement>(':scope > [data-primary]');
    const more = row.querySelector<HTMLElement>(':scope > [data-slot="page-header-more"]');
    const fit = () => {
      const style = getComputedStyle(row);
      const gap = parseFloat(style.columnGap) || 0;
      const pad = (parseFloat(style.paddingInlineStart) || 0) + (parseFloat(style.paddingInlineEnd) || 0);
      const width = (pinned?.offsetWidth ?? 0) + (more ? more.offsetWidth + (pinned ? gap : 0) : 0);
      row.style.minWidth = width ? `${Math.ceil(width + pad)}px` : "";
    };
    fit();
    const observer = new ResizeObserver(fit);
    if (pinned) observer.observe(pinned);
    return () => observer.disconnect();
  }, [primaryKey, hasSecondaries, loading, isMobile]);

  // promises returned by onSelect keep their button loading
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const run = useCallback((action: PageHeaderAction, key: string) => {
    const result = action.onSelect?.();
    if (!isThenable(result)) return;
    setPending((p) => ({ ...p, [key]: true }));
    const done = () => {
      if (mounted.current) setPending((p) => ({ ...p, [key]: false }));
    };
    result.then(done, done);
  }, []);

  const menuEntry = (action: PageHeaderAction, key: string): MenuEntry =>
    action.items
      ? { type: "submenu", id: key, label: action.label, icon: action.icon, disabled: action.disabled, items: action.items }
      : menuEntryFor(action, key);
  const menuEntryFor = (action: PageHeaderAction, key: string): MenuEntry => ({
    id: key,
    label: action.label,
    icon: action.icon,
    disabled: action.disabled || pending[key],
    tone: action.tone === "danger" ? "danger" : "neutral",
    onSelect: () => {
      if (action.href) {
        action.onSelect?.();
        navigate(action.href);
      } else run(action, key);
    },
  });

  // ---- condensed bar: shown once the header has scrolled under the top bar
  const headerRef = useRef<HTMLElement | null>(null);
  const [condensed, setCondensed] = useState(false);
  const condensing = sticky === "condensed";
  const showBar = condensing && condensed;
  useEffect(() => {
    const el = headerRef.current;
    if (!condensing || !el || typeof IntersectionObserver === "undefined") return;
    let root: Element | null = null;
    // default: the provider's container, else the nearest scrolling ancestor (null: the window)
    if (scrollTarget === undefined) root = provided ?? findScrollParent(el);
    else if (scrollTarget && "current" in scrollTarget) root = scrollTarget.current;
    else if (scrollTarget instanceof HTMLElement) root = scrollTarget;
    // the top bar writes its visible height on its parent; the header reads it through inheritance
    const offset = parseFloat(getComputedStyle(el).getPropertyValue("--topbar-offset")) || 0;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const top = entry.rootBounds?.top ?? offset;
        setCondensed(!entry.isIntersecting && entry.boundingClientRect.bottom <= top + 1);
      },
      { root, rootMargin: `${-Math.round(offset)}px 0px 0px 0px`, threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [condensing, scrollTarget, provided]);

  const setHeaderRef = useCallback(
    (el: HTMLElement | null) => {
      headerRef.current = el;
      return setRef(ref as Ref<HTMLElement | null> | undefined, el);
    },
    [ref],
  );

  // ---- pieces
  const hasBack = Boolean(backHref || onBack);
  const hasBreadcrumb = Array.isArray(breadcrumb) ? breadcrumb.length > 0 : filled(breadcrumb as ReactNode);
  const hasMedia = Boolean(avatar) || filled(icon);
  const hasActions = count > 0 || customActions !== null;
  const hasDescription = filled(description);
  const metaList = Array.isArray(meta) ? (meta as PageHeaderMeta[]) : null;
  const hasMeta = metaList ? metaList.length > 0 : filled(meta as ReactNode);
  const titleText = typeof title === "string" ? title : undefined;
  // a title alone next to a tile reads better centered on it
  const centered = hasMedia && !hasDescription && !hasMeta;

  const backContent = (phone: boolean) =>
    phone ? (
      <>
        <ChevronLeft aria-hidden className="size-[18px] shrink-0 rtl:rotate-180" />
        <span className="min-w-0 truncate">{backLabel}</span>
      </>
    ) : (
      <ChevronLeft aria-hidden className="rtl:rotate-180" />
    );

  const phoneBack = hasBack ? (
    backHref ? (
      <NavLink
        data-slot="page-header-back"
        href={backHref}
        component={linkComponent}
        onClick={() => onBack?.()}
        className={cn(BACK_LINK, classNames?.back)}
      >
        {backContent(true)}
      </NavLink>
    ) : (
      <button type="button" data-slot="page-header-back" onClick={() => onBack?.()} className={cn(BACK_LINK, classNames?.back)}>
        {backContent(true)}
      </button>
    )
  ) : null;

  const desktopBack = hasBack ? (
    <div
      className={cn(
        "flex shrink-0 items-center max-sm:hidden",
        avatar ? AVATAR_HEIGHTS[scale] : hasMedia ? TILE_HEIGHTS[scale] : LINE_HEIGHTS[scale],
        centered && "h-auto",
      )}
    >
      {backHref ? (
        <Button
          asChild
          variant="outline"
          size="icon-sm"
          radius="full"
          aria-label={backLabel}
          className={cn("-ms-0.5", classNames?.back)}
          data-slot="page-header-back"
        >
          <NavLink href={backHref} component={linkComponent} onClick={() => onBack?.()}>
            {backContent(false)}
          </NavLink>
        </Button>
      ) : (
        <Button
          variant="outline"
          size="icon-sm"
          radius="full"
          aria-label={backLabel}
          onClick={() => onBack?.()}
          className={cn("-ms-0.5", classNames?.back)}
          data-slot="page-header-back"
        >
          {backContent(false)}
        </Button>
      )}
    </div>
  ) : null;

  const media = loading && hasMedia ? (
    <Skeleton
      data-slot="page-header-media"
      shape={avatar && (avatar.radius ?? "md") === "full" ? "circle" : "rect"}
      radius={scale === "sm" ? "md" : "lg"}
      width={avatar ? AVATAR_SIZES[scale] : scale === "lg" ? 48 : scale === "sm" ? 36 : 40}
      height={avatar ? AVATAR_SIZES[scale] : scale === "lg" ? 48 : scale === "sm" ? 36 : 40}
      className={cn("shrink-0", classNames?.media)}
    />
  ) : avatar ? (
    <Avatar
      data-slot="page-header-media"
      src={avatar.src}
      name={avatar.name}
      size={AVATAR_SIZES[scale]}
      radius={avatar.radius ?? "md"}
      tone={tone}
      color={color}
      className={cn("shrink-0", classNames?.media)}
    />
  ) : filled(icon) ? (
    <span
      data-slot="page-header-media"
      data-tone={color ? "custom" : tone}
      aria-hidden
      className={cn(
        "flex shrink-0 items-center justify-center bg-[color:var(--tone-soft)] text-[color:var(--tone-soft-foreground)]",
        toneClasses[tone] ?? toneClasses.neutral,
        TILE_SIZES[scale],
        classNames?.media,
      )}
      style={colorVars(color)}
    >
      {icon}
    </span>
  ) : null;

  const heading = (
    <div data-slot="page-header-heading" className={cn("flex flex-wrap items-center gap-x-2.5 gap-y-1", classNames?.heading)}>
      <h1
        data-slot="page-header-title"
        className={cn("min-w-0 font-semibold tracking-tight text-balance text-foreground", TITLE_SIZES[scale], classNames?.title)}
      >
        {loading ? (
          <>
            <span className="sr-only">{titleText}</span>
            <Skeleton radius="md" className={cn("my-1", TITLE_SKELETONS[scale])} />
          </>
        ) : (
          title
        )}
      </h1>
      {!loading && filled(badges) && (
        <span data-slot="page-header-badges" className={cn("flex flex-wrap items-center gap-1.5", classNames?.badges)}>
          {badges}
        </span>
      )}
    </div>
  );

  const descriptionNode = hasDescription ? (
    loading ? (
      <Skeleton shape="line" className="mt-2.5 h-3.5 w-[min(100%,380px)]" />
    ) : (
      <div
        data-slot="page-header-description"
        className={cn("mt-1 max-w-prose text-sm text-pretty text-muted-foreground", classNames?.description)}
      >
        {description}
      </div>
    )
  ) : null;

  const metaNode = hasMeta ? (
    loading ? (
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
        {[96, 132, 110].map((w) => (
          <Skeleton key={w} shape="line" className="h-3" width={w} />
        ))}
      </div>
    ) : metaList ? (
      <ul
        data-slot="page-header-meta"
        className={cn("mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-muted-foreground", classNames?.meta)}
      >
        {metaList.map((item, i) => (
          <li
            key={item.id ?? i}
            data-slot="page-header-meta-item"
            className={cn(
              "inline-flex min-w-0 items-center gap-1.5 [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5",
              classNames?.metaItem,
            )}
          >
            {item.icon}
            <span className="min-w-0">{item.label}</span>
          </li>
        ))}
      </ul>
    ) : (
      <div data-slot="page-header-meta" className={cn("mt-2 text-[13px] text-muted-foreground", classNames?.meta)}>
        {meta as ReactNode}
      </div>
    )
  ) : null;

  // ---- action buttons
  const actionButton = (
    action: PageHeaderAction,
    key: string,
    options: { isPrimary: boolean; size: "sm" | "md" | "lg"; fullWidth?: boolean; extra?: Record<string, unknown>; slot?: string; tabIndex?: number },
  ) => {
    const variant = action.variant ?? (options.isPrimary ? "solid" : "outline");
    const shared = {
      variant,
      tone: action.tone ?? "neutral",
      size: options.size,
      disabled: action.disabled,
      fullWidth: options.fullWidth,
      leftIcon: action.icon,
      tabIndex: options.tabIndex,
      "data-slot": options.slot ?? "page-header-action",
      "data-primary": options.isPrimary || undefined,
      ...options.extra,
    };
    if (action.items)
      return (
        <DropdownMenu
          key={key}
          align="end"
          items={action.items}
          title={action.label}
          cancelLabel={cancelLabel}
          trigger={
            <Button {...shared} rightIcon={<ChevronDown />}>
              {action.label}
            </Button>
          }
        />
      );
    if (action.href)
      return (
        <Button key={key} asChild {...shared}>
          <NavLink href={action.href} component={linkComponent} disabled={action.disabled} onClick={() => action.onSelect?.()}>
            {action.label}
          </NavLink>
        </Button>
      );
    return (
      <Button key={key} {...shared} loading={Boolean(pending[key])} onClick={() => run(action, key)}>
        {action.label}
      </Button>
    );
  };

  const hiddenSecondaries = [...hiddenIndexes.filter((i) => i < secondaries.length).map((i) => secondaries[i]), ...folded];
  const anyHiddenPending = hiddenSecondaries.some((s) => pending[s.key]);
  const menuTitle = titleText || undefined;

  const moreTrigger = (size: "icon-sm" | "icon", loadingMore: boolean, extra?: Record<string, unknown>) => (
    <Button
      variant="outline"
      size={size}
      aria-label={moreLabel}
      loading={loadingMore}
      data-slot="page-header-more"
      className={classNames?.more}
      {...extra}
    >
      <Ellipsis />
    </Button>
  );

  const desktopActions =
    count > 0 ? (
      <div
        ref={setActionsRow}
        data-slot="page-header-actions"
        className={cn(
          // the padding keeps focus rings inside the clip; the negative margin cancels it
          "relative -m-1.5 hidden min-w-0 flex-1 items-center justify-end gap-2 overflow-hidden p-1.5 @lg/page-header:flex",
          classNames?.actions,
        )}
      >
        {secondaries.map(({ action, key }, i) =>
          actionButton(action, key, { isPrimary: false, size: "md", extra: getItemProps(i) as Record<string, unknown> }),
        )}
        {allSecondaries.length > 0 && (
          <DropdownMenu
            align="end"
            items={hiddenSecondaries.map(({ action, key }) => menuEntry(action, key))}
            title={menuTitle}
            sheetTitle={moreLabel}
            cancelLabel={cancelLabel}
            // folded actions keep the trigger on screen whatever fits
            trigger={moreTrigger("icon", anyHiddenPending, {
              ...(getMoreProps() as Record<string, unknown>),
              ...(folded.length > 0 ? { "data-overflow": "visible", "aria-hidden": undefined, inert: undefined, style: undefined } : null),
            })}
          />
        )}
        {primary && actionButton(primary, primaryKey, { isPrimary: true, size: "md", extra: getItemProps(secondaries.length) as Record<string, unknown> })}
      </div>
    ) : null;

  const phoneActions =
    count > 0 ? (
      mobileActions === "stack" ? (
        <div data-slot="page-header-actions" data-layout="stack" className={cn("flex w-full flex-col gap-2 @lg/page-header:hidden", classNames?.actions)}>
          {primary && actionButton(primary, primaryKey, { isPrimary: true, size: "lg", fullWidth: true })}
          {allSecondaries.map(({ action, key }) => actionButton(action, key, { isPrimary: false, size: "lg", fullWidth: true }))}
        </div>
      ) : (
        <div data-slot="page-header-actions" data-layout="menu" className={cn("flex items-center gap-2 @lg/page-header:hidden", classNames?.actions)}>
          {primary && actionButton(primary, primaryKey, { isPrimary: true, size: "sm" })}
          {allSecondaries.length > 0 && (
            <DropdownMenu
              align="end"
              items={allSecondaries.map(({ action, key }) => menuEntry(action, key))}
              title={menuTitle}
              sheetTitle={moreLabel}
              cancelLabel={cancelLabel}
              trigger={moreTrigger("icon-sm", allSecondaries.some((s) => pending[s.key]))}
            />
          )}
        </div>
      )
    ) : null;

  const actionSkeletons = (
    <>
      <div className="hidden shrink-0 items-center gap-2 @lg/page-header:flex">
        {primary && allSecondaries.length > 0 && <Skeleton radius="md" width={96} height={40} />}
        <Skeleton radius="md" width={128} height={40} />
      </div>
      <div className="flex items-center gap-2 @lg/page-header:hidden">
        <Skeleton radius="md" width={mobileActions === "stack" ? "100%" : 128} height={mobileActions === "stack" ? 48 : 32} />
      </div>
    </>
  );

  // ---- condensed bar content
  const condensedMotion = reduced
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1, transition: { duration: durations.fast } },
        exit: { opacity: 0, transition: { duration: durations.fast } },
      }
    : {
        initial: { y: "-100%", opacity: 0 },
        animate: { y: 0, opacity: 1, transition: springs.smooth },
        exit: { y: "-100%", opacity: 0, transition: { duration: durations.fast, ease: easings.out } },
      };

  const condensedMedia = avatar ? (
    <Avatar
      src={avatar.src}
      name={avatar.name}
      size={24}
      radius={avatar.radius === "full" ? "full" : "sm"}
      tone={tone}
      color={color}
      className="shrink-0"
    />
  ) : filled(icon) ? (
    <span
      className={cn(
        "flex size-6 shrink-0 items-center justify-center rounded-[8px] bg-[color:var(--tone-soft)] text-[color:var(--tone-soft-foreground)] [&_svg:not([class*='size-'])]:size-3.5",
        toneClasses[tone] ?? toneClasses.neutral,
      )}
      style={colorVars(color)}
    >
      {icon}
    </span>
  ) : null;

  return (
    <>
      <header
        ref={setHeaderRef}
        data-slot="page-header"
        data-size={scale}
        data-condensed={condensing ? String(showBar) : undefined}
        data-loading={loading || undefined}
        aria-busy={loading || undefined}
        className={cn(
          // a container: the title row and the actions switch between the phone and the desktop layout by the header's own
          // width, so it also works in a split view or a side panel
          "@container/page-header flex w-full min-w-0 flex-col gap-4 pt-5 md:pt-8",
          border && "border-b border-foreground/10",
          !filled(tabs) && "pb-5",
          className,
        )}
        {...props}
      >
        {(hasBreadcrumb || hasBack) && (
          <div data-slot="page-header-breadcrumb" className={cn("flex min-h-5 min-w-0 items-center", classNames?.breadcrumb)}>
            {hasBack && <div className="flex min-w-0 flex-1 sm:hidden">{phoneBack}</div>}
            {hasBreadcrumb && (
              <div className={cn("min-w-0 flex-1", hasBack && "max-sm:hidden")}>
                {Array.isArray(breadcrumb) ? (
                  <Breadcrumb items={breadcrumb as BreadcrumbEntry[]} size="sm" linkComponent={linkComponent} />
                ) : (
                  (breadcrumb as ReactNode)
                )}
              </div>
            )}
          </div>
        )}

        <div
          data-slot="page-header-row"
          className={cn("flex min-w-0 flex-col gap-3 @lg/page-header:flex-row @lg/page-header:items-start @lg/page-header:gap-x-6", classNames?.row)}
        >
          <div
            className={cn(
              "flex min-w-0 gap-3 sm:gap-3.5",
              centered ? "items-center" : "items-start",
              // folding actions need room to measure: the title block takes its natural width up to 65%
              count > 0 ? "@lg/page-header:max-w-[65%] @lg/page-header:flex-[0_1_auto]" : "flex-1",
            )}
          >
            {desktopBack}
            {media}
            <div className="min-w-0 flex-1">
              {heading}
              {descriptionNode}
              {metaNode}
            </div>
          </div>

          {loading && hasActions
            ? actionSkeletons
            : customActions !== null
              ? (
                  <div
                    data-slot="page-header-actions"
                    className={cn("flex flex-wrap items-center gap-2 @lg/page-header:shrink-0 @lg/page-header:flex-nowrap", classNames?.actions)}
                  >
                    {customActions}
                  </div>
                )
              : (
                  <>
                    {desktopActions}
                    {phoneActions}
                  </>
                )}
        </div>

        {filled(children) && children}

        {filled(tabs) && (
          <div data-slot="page-header-tabs" className={cn("-mb-px min-w-0", classNames?.tabs)}>
            {tabs}
          </div>
        )}
      </header>

      {condensing && (
        // a zero-height sticky anchor right after the header: it pins under the top bar, the bar hangs from it
        <div
          data-slot="page-header-condensed"
          aria-hidden
          className={cn("pointer-events-none sticky top-[var(--topbar-offset,0px)] z-sticky -mx-4 h-0 md:-mx-6", classNames?.condensed)}
        >
          <div className="absolute inset-x-0 top-0 h-[49px] overflow-hidden">
            <AnimatePresence>
              {showBar && (
                <motion.div
                  key="bar"
                  {...condensedMotion}
                  data-slot="page-header-condensed-bar"
                  className={cn(
                    "pointer-events-auto flex h-12 items-center gap-2.5 border-b border-foreground/10 px-4 md:px-6",
                    BAR_GLASS,
                  )}
                >
                  {condensedMedia}
                  <span
                    data-slot="page-header-condensed-title"
                    className={cn("min-w-0 flex-1 truncate text-[15px] font-semibold tracking-tight", classNames?.condensedTitle)}
                  >
                    {title}
                  </span>
                  {primary &&
                    actionButton(primary, primaryKey, {
                      isPrimary: true,
                      size: "sm",
                      slot: "page-header-condensed-action",
                      tabIndex: -1,
                      extra: { className: classNames?.condensedAction },
                    })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}
    </>
  );
}

// React 18: ref-urile ajung la randare doar prin forwardRef (vezi lib/with-ref.ts)
export const PageHeader = withRef(PageHeaderRender, "PageHeader");

export default PageHeader;
